// Tetromino shapes + pure helpers, shared so the client can preview a
// rotation/move locally-informed by the same rules the server validates
// placement with. No wall-kick/SRS rotation system — "best effort" per the
// design doc: a rotation that doesn't fit is simply rejected.

export type PieceType = "I" | "O" | "T" | "S" | "Z" | "J" | "L";

export const PIECE_TYPES: readonly PieceType[] = ["I", "O", "T", "S", "Z", "J", "L"];

export interface Offset {
  dx: number;
  dy: number;
}

const PIECE_SHAPES: Record<PieceType, Offset[]> = {
  I: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 2, dy: 0 }, { dx: 3, dy: 0 }],
  O: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 }],
  T: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 2, dy: 0 }, { dx: 1, dy: 1 }],
  S: [{ dx: 1, dy: 0 }, { dx: 2, dy: 0 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 }],
  Z: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 1, dy: 1 }, { dx: 2, dy: 1 }],
  J: [{ dx: 0, dy: 0 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 }, { dx: 2, dy: 1 }],
  L: [{ dx: 2, dy: 0 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 }, { dx: 2, dy: 1 }],
};

export function pieceOffsets(type: PieceType): Offset[] {
  return PIECE_SHAPES[type].map((o) => ({ ...o }));
}

function normalize(offsets: Offset[]): Offset[] {
  const minDx = Math.min(...offsets.map((o) => o.dx));
  const minDy = Math.min(...offsets.map((o) => o.dy));
  return offsets.map((o) => ({ dx: o.dx - minDx, dy: o.dy - minDy }));
}

/** Rotates a shape 90° clockwise within its own bounding box. */
export function rotateOffsets(offsets: Offset[]): Offset[] {
  const maxDx = Math.max(...offsets.map((o) => o.dx));
  return normalize(offsets.map((o) => ({ dx: o.dy, dy: maxDx - o.dx })));
}

export function pieceWidth(offsets: Offset[]): number {
  return Math.max(...offsets.map((o) => o.dx)) + 1;
}

export function pieceHeight(offsets: Offset[]): number {
  return Math.max(...offsets.map((o) => o.dy)) + 1;
}
